/**
 * @file 订单相关
 * @author caiguoqing
 */

import axiosInstance from "./axiosInstance";
import { message } from "antd";
import { t } from "i18next";
import type { CreatorTranslation } from "@/interface/translation";
import type { FilterCountOption } from "@/interface/filter";
import type { ShippingPackagesInput } from "@/interface/shipping";
import type {
  AdsNegotiateRequest,
  ConfirmDraftRequest,
  DeliveryContentRound,
  FeedbackRequest,
  PackagePriceRecommendation,
  PackagePriceRecommendationRequest,
} from "@/interface/orderV2";

// Translate advertiser-authored content into the creator's language.
// The backend resolves targetLanguage from the order identified by orderId.
export const translateOrderContentApi = async (
  orderId: string,
  content: string,
): Promise<CreatorTranslation> => {
  const response = await axiosInstance.post(
    `/ads/order/${orderId}/translate`,
    { content },
  );
  return response.data.data;
};

// 获取待做的订单
export const getCampaignTodosApi = async (
  campaignId: string,
  filter: { cycleId: string; marketingStrategy: string },
) => {
  let url = `/ads/order/pending`;

  if (filter.marketingStrategy) {
    url = `/ads/order/pending?marketingStrategy=${filter.marketingStrategy}`;
  }

  try {
    // console.log('[indexxuan]: getTodos call', url, new Date());
    const response = await axiosInstance.post(url, {
      campaignId: +campaignId,
      ...filter,
    });
    return response.data.data;
  } catch (error: any) {
    throw new Error(error?.message);
  }
};

// 获取campaign下订单统计数据
export const getCampaignOrderStatsApi = async (
  campaignId: string,
  cycleId: string,
) => {
  try {
    const response = await axiosInstance.post(
      `/ads/order/${campaignId}/stats`,
      {
        cycleId,
      },
    );
    return response.data.data;
  } catch (error: any) {
    throw new Error(error?.message || error?.error);
  }
};

// 获取所有订单
export const getAllOrderApi = async (campaignId: string, params: any) => {
  let url = `/ads/order/groupList`;
  if (params.marketingStrategy) {
    url = `/ads/order/groupList?marketingStrategy=${params.marketingStrategy}`;
  }

  try {
    const response = await axiosInstance.post(url, {
      campaignId: +campaignId,
      ...params,
    });
    return response.data.data;
  } catch (error: any) {
    throw new Error(error?.message);
  }
};

// 查询订单列表按 Tab 分页（仅当前 group 一页数据 + total）
export const getAllOrderPageApi = async (campaignId: string, params: any) => {
  let url = `/ads/v2/order/groupList/page`;
  if (params.marketingStrategy) {
    url = `/ads/v2/order/groupList/page?marketingStrategy=${params.marketingStrategy}`;
  }

  try {
    const response = await axiosInstance.post(url, {
      campaignId: +campaignId,
      ...params,
    });
    return response.data.data;
  } catch (error: any) {
    throw new Error(error?.message);
  }
};

// 查询订单列表每个tab的数量
export const getAllOrderStatsApi = async (campaignId: string, params: any) => {
  let url = `/ads/v2/order/groupList/stats`;
  if (params.marketingStrategy) {
    url = `/ads/v2/order/groupList/stats?marketingStrategy=${params.marketingStrategy}`;
  }

  try {
    const response = await axiosInstance.post(url, {
      campaignId: +campaignId,
      ...params,
    });
    return response.data.data;
  } catch (error: any) {
    throw new Error(error?.message);
  }
};

export type OrderGroupListFilterOption = {
  label: string;
  value: string;
};

export type OrderGroupListFilterOptions = {
  influencerProfiles?: { id: string; title: string; count?: number }[];
  languages?: FilterCountOption[];
  audienceLocations?: FilterCountOption[];
  notes?: OrderGroupListFilterOption[];
};

// 查询订单列表筛选项
export const getOrderGroupListFilterApi = async (
  campaignId: string,
  params: any,
): Promise<OrderGroupListFilterOptions> => {
  const { cycleId, marketingStrategy } = params || {};
  let url = `/ads/v2/order/groupList/filter?campaignId=${campaignId}&cycleId=${cycleId}`;
  if (marketingStrategy) {
    url += `&marketingStrategy=${marketingStrategy}`;
  }

  try {
    const response = await axiosInstance.get(url);
    return response.data.data;
  } catch (error: any) {
    throw new Error(error?.message);
  }
};

// 查询订单todo列表
export const getTodoOrderApi = async () => {
  const url = `/ads/v2/order/todo`;
  try {
    const response = await axiosInstance.get(url);
    return response.data.data;
  } catch (error: any) {
    // axios 拦截器：业务错误 reject({ code, error })，HTTP 错误 reject({ message, ... })
    const msg = error?.message ?? error?.error;
    throw new Error(
      msg != null && String(msg).length > 0
        ? String(msg)
        : "getTodoOrderApi failed",
    );
  }
};

// 获取订单详情
// GET /ads/v2/order/{id}
export const getOrderDetailApi = async (id: string, lang?: string) => {
  const languageQuery = lang ? `?lang=${encodeURIComponent(lang)}` : "";
  const url = `/ads/v2/order/${id}${languageQuery}`;
  try {
    const response = await axiosInstance.get(url);
    return response.data.data;
  } catch (error: any) {
    throw error;
  }
};

// 获取订单交付时间线内容
// GET /ads/v2/order/{id}/deliveryContent
export const getOrderDeliveryContentApi = async (
  id: string,
): Promise<DeliveryContentRound[]> => {
  const url = `/ads/v2/order/${id}/deliveryContent`;
  try {
    const response = await axiosInstance.get(url);
    return response.data.data;
  } catch (error: any) {
    throw error;
  }
};

// 通过达人
// POST /ads/v2/order/{id}/confirm
export const confirmOrderApi = async (
  id: string,
  params: { lastBid?: number },
) => {
  const url = `/ads/v2/order/${id}/confirm`;
  try {
    const response = await axiosInstance.post(url, params);
    return response.data.data;
  } catch (error: any) {
    if (error?.code === 40012) {
      throw {
        code: error?.code,
        message: error?.message || error?.error,
      };
    } else {
      return error;
    }
  }
};

// 拒绝达人
// POST /ads/v2/order/{id}/reject
export const rejectOrderApi = async (id: string, reason: string[]) => {
  const url = `/ads/v2/order/${id}/reject`;
  try {
    const response = await axiosInstance.post(url, {
      reason,
    });
    return response.data.data;
  } catch (error: any) {
    return error;
  }
};

// 通过达人并增加预算
export const confirmAndIncBudgetApi = async (id: string) => {
  try {
    const response = await axiosInstance.post(
      `/ads/order/${id}/confirmAndIncBudget`,
    );
    return response.data.data;
  } catch (error: any) {
    return error;
  }
};

// 批量确认订单并增加预算
export const batchConfirmAndIncBudgetApi = async (params: {
  orderIds: string[];
  campaignId: string;
}) => {
  try {
    const response = await axiosInstance.post(
      `/ads/order/batchConfirmAndIncBudget`,
      params,
    );
    return response.data.data;
  } catch (error: any) {
    return error;
  }
};

// 查询付款状态 轮训
// POST /ads/order/{id}/confirm
export const getChargeStatusApi = async (id: string) => {
  try {
    const response = await axiosInstance.get(`/ads/order/chargeStatus/${id}`);
    return response.data.data;
  } catch (error: any) {
    return error;
  }
};

// 确认稿件
// POST /ads/v2/order/{id}/confirmDraft
export const confirmDraftOrderApi = async (
  id: string,
  params: ConfirmDraftRequest,
) => {
  const url = `/ads/v2/order/${id}/confirmDraft`;
  try {
    const response = await axiosInstance.post(url, params);
    // Keep the response envelope so callers can handle business codes such as 40009.
    return response.data;
  } catch (error: any) {
    return error;
  }
};

// 提交反馈
// POST /ads/v2/order/{id}/feedback
export const feedbackOrderApi = async (
  id: string,
  params: FeedbackRequest,
) => {
  const url = `/ads/v2/order/${id}/feedback`;
  try {
    const response = await axiosInstance.post(url, params);
    // Keep the response envelope so callers can handle business codes such as 40009.
    return response.data;
  } catch (error: any) {
    return error;
  }
};

// 修改反馈意见
export const editFeedbackOrderApi = async (
  id: string,
  params: FeedbackRequest,
) => {
  try {
    const response = await axiosInstance.put(`/ads/v2/order/${id}/feedback`, params);
    return response.data;
  } catch (error: any) {
    return error;
  }
};

// 延期
// POST /ads/order/{id}/postpone
export const postponeOrderApi = async (id: string) => {
  try {
    const response = await axiosInstance.post(`/ads/order/${id}/postpone`);
    return response.data.data;
  } catch (error: any) {
    return error;
  }
};

// 完成订单
// POST /ads/order/{id}/complete
export const completeOrderApi = async (id: string) => {
  try {
    const response = await axiosInstance.post(`/ads/order/${id}/complete`);
    return response.data.data;
  } catch (error: any) {
    throw error;
  }
};

export const checkIntegrationStatus = async (
  campaignId: string,
  clientId: string,
) => {
  try {
    const response = await axiosInstance.post(
      `/order/testTracking/${campaignId}`,
      { clientId },
    );
    return response.data.data;
  } catch (error: any) {
    throw error;
  }
};

// feedback 上传图片、视频接口
export const feedbackMediaUploadApi = async (
  id: string,
  params: { key: string },
) => {
  try {
    const response = await axiosInstance.post(
      `/ads/order/${id}/publicUploadLink`,
      params,
    );
    return response.data.data;
  } catch (error: any) {
    throw error;
  }
};

// 获取所有物流公司
export const getLogisticsCompanyApi = async () => {
  try {
    const response = await axiosInstance.get(`/ads/order/couriers`);
    return response.data.data;
  } catch (error: any) {
    throw error;
  }
};
// 提交物流订单号
export const trackingNumberApi = async (
  id: string,
  params: ShippingPackagesInput,
) => {
  try {
    const response = await axiosInstance.post(
      `/ads/v2/order/${id}/trackingNumber`,
      params,
    );
    return response.data.data;
  } catch (error: any) {
    throw error;
  }
};

// 校验物流订单号
export const checkTrackingNumberApi = async (params: {
  courierCode: string;
  trackingNumber: string;
}) => {
  try {
    const response = await axiosInstance.post(
      `/ads/order/checkTrackingNumber`,
      params,
    );
    return response.data.data;
  } catch (error: any) {
    throw error;
  }
};

// 创建聊天室
export const createImApi = async (id: string) => {
  try {
    const response = await axiosInstance.post(`/ads/im/group/${id}`, {});
    return response.data.data;
  } catch (error: any) {
    throw error;
  }
};
// 取消订单
export const cancelOrderApi = async (id: string) => {
  try {
    const response = await axiosInstance.post(`/ads/v2/order/${id}/cancel`, {});
    return response.data.data;
  } catch (error: any) {
    throw error;
  }
};
// 订单反馈
export const orderFeedbackApi = async (
  orderId: string,
  params: { text: string },
) => {
  try {
    const response = await axiosInstance.post(
      `/ads/feedback/order/${orderId}`,
      params,
    );
    return response.data.data;
  } catch (error: any) {
    throw error;
  }
};

// 更改订单配置
export const changeOrderConfigApi = async (
  id: string,
  params: { isStar: boolean },
) => {
  try {
    const response = await axiosInstance.post(
      `/ads/v2/order/${id}/config`,
      params,
    );
    return response.data.data;
  } catch (error: any) {
    throw error;
  }
};

// 获取所有要求的达人
export const getInvitedCreatorList = async (
  campaignId: string,
  params: {
    paginate: { pageNum: number; pageLimit: number };
    name: string;
    marketingStrategy: string;
  },
) => {
  let url = `/ads/order/${campaignId}/invited`;

  if (params.marketingStrategy) {
    url = `/ads/order/${campaignId}/invited?marketingStrategy=${params.marketingStrategy}`;
  }

  try {
    const response = await axiosInstance.post(url, params);
    return response.data.data;
  } catch (error: any) {
    throw error;
  }
};

export interface AdsReportOrderRequest {
  types: string[];
  reason: string;
  urls: string[];
  orderId: string;
  blockInfluencer?: boolean;
}

// 举报达人
export const adsReportOrderApi = async (
  creatorId: string,
  params: AdsReportOrderRequest,
) => {
  try {
    const response = await axiosInstance.post(
      `ads/feedback/influencer/${creatorId}/report`,
      params,
    );
    return response.data.data;
  } catch (error: any) {
    throw error;
  }
};

// IM已读消息回调
export const imMessageReadApi = async (params: {
  userId: string;
  groupId: string;
}) => {
  try {
    const response = await axiosInstance.post(`api/mark-messages-read`, params);
    return response.data.data;
  } catch (error: any) {
    throw error;
  }
};

// 获取Email outtreach
export const emailOuttreachLeadsInfoApi = async (campaignid: string) => {
  try {
    const response = await axiosInstance.get(
      `ads/campaign/${campaignid}/leadsInfo`,
    );
    return response.data.data;
  } catch (error: any) {
    throw error;
  }
};

// 获取Email outtreach list
export const emailOuttreachListApi = async (
  campaignid: string,
  params: { paginate: { pageNum: number; pageLimit: number } },
) => {
  try {
    const response = await axiosInstance.post(
      `ads/campaign/${campaignid}/leads`,
      params,
    );
    return response.data;
  } catch (error: any) {
    throw error;
  }
};

// 大客户获取campaign email sample
export const getCampaignEmailSampleApi = async (campaignid: string) => {
  try {
    const response = await axiosInstance.get(
      `ads/campaign/${campaignid}/emailSample`,
    );
    return response.data;
  } catch (error: any) {
    throw error;
  }
};

// 获取commission列表
export const getCommissionApi = async (
  campaignId: string,
  params: {
    paginate: { pageNum: number; pageLimit: number };
    cycleId: string;
    status: string;
    name: string;
  },
) => {
  try {
    const response = await axiosInstance.post(
      `/ads/campaign/${campaignId}/commisions`,
      params,
    );
    return response.data;
  } catch (error: any) {
    throw error;
  }
};

// 获取affiliate 订单价格
export const getCommissionChargeDetailApi = async (
  campaignId: string,
  params: {
    commisions: string[];
  },
) => {
  try {
    const response = await axiosInstance.post(
      `/ads/campaign/${campaignId}/commisions/chargeDetail`,
      params,
    );
    return response.data;
  } catch (error: any) {
    throw error;
  }
};

// 支付affiliate 订单
export const payCommissionApi = async (
  campaignId: string,
  params: {
    commisions: string[];
  },
) => {
  try {
    const response = await axiosInstance.post(
      `/ads/campaign/${campaignId}/commisions/pay`,
      params,
    );
    return response.data;
  } catch (error: any) {
    throw error;
  }
};

// 广告主确认达人后，初步沟通创作方向
export const creativeDirectionApi = async (
  campaignId: string,
  params: {
    content: string;
    creatorTranslation?: CreatorTranslation;
  },
) => {
  try {
    const response = await axiosInstance.post(
      `/ads/v2/order/${campaignId}/creativeDirection`,
      params,
    );
    return response.data;
  } catch (error: any) {
    const errorMessage = [error?.message, error?.error].find(
      (value) => typeof value === "string" && value.trim(),
    );
    message.error(
      errorMessage ||
        t("components.modalContentGuidance.creativeDirectionFailedMessage"),
    );
    throw error;
  }
};
// 更新达人申请自动审核设置
export const updateApplyAutoReviewConfigApi = async (
  campaignId: string,
  params: {
    matchingScoreAbove: number;
    cpmBelow: any;
    cpcBelow: any;
  },
) => {
  try {
    const response = await axiosInstance.post(
      `/ads/campaign/v3/${campaignId}/updateApplyAutoReviewConfig`,
      params,
    );
    return response.data;
  } catch (error: any) {
    throw error;
  }
};

// 下载达人列表
export const downloadCreatorListApi = async (params: any) => {
  try {
    const response = await axiosInstance.post(
      `/ads/v2/order/groupList/download?marketingStrategy=${params.marketingStrategy}`,
      params,
      { responseType: "blob" },
    );
    return response;
  } catch (error: any) {
    throw error;
  }
};

// 计算传入的订单id数组，申请审核过的数量
export const getApplyReviewedCountApi = async (params: { ids: string[] }) => {
  try {
    const response = await axiosInstance.post(
      `/ads/order/getApplyReviewedCount`,
      params,
    );
    return response.data;
  } catch (error: any) {
    throw error;
  }
};

// 获取传入的订单id数组的价格
export const getTotalChargeDetailApi = async (params: {
  orderIds: string[];
  campaignId: string;
}) => {
  try {
    const response = await axiosInstance.post(
      `/ads/order/getTotalChargeDetail`,
      params,
    );
    return response.data;
  } catch (error: any) {
    throw error;
  }
};

// 批量拒绝订单
export const batchRejectApi = async (params: {
  orderIds: React.Key[];
  campaignId: string;
  reason: string[];
}) => {
  try {
    const response = await axiosInstance.post(`/ads/order/batchReject`, params);
    return response.data;
  } catch (error: any) {
    throw error;
  }
};
// 批量通过订单
export const batchConfirmOrderApi = async (params: {
  campaignId: string;
  orders?: { orderId: string; lastBid?: number }[];
}) => {
  try {
    const response = await axiosInstance.post(
      `/ads/order/batchConfirm`,
      params,
    );
    return response.data.data;
  } catch (error: any) {
    if (error?.code === 40012) {
      throw {
        code: error?.code,
        message: error?.message || error?.error,
      };
    } else {
      return error;
    }
  }
};

// 审核达人申请延期
export const reviewPostponeApi = async (
  orderId: string,
  params: {
    approve: Boolean;
    reason?: string;
    creatorTranslation?: CreatorTranslation;
  },
) => {
  try {
    const response = await axiosInstance.post(
      `/ads/v2/order/${orderId}/reviewPostpone`,
      params,
    );
    return response.data;
  } catch (error: any) {
    return error;
  }
};

// 批量通过达人延期申请
export const batchPostponeApi = async (params: { orderIds: string[] }) => {
  const response = await axiosInstance.post(
    `/ads/order/batchPostpone`,
    params,
  );
  return response.data.data;
};

// 评价订单
export const reviewOrderApi = async (
  orderId: string,
  params: {
    rating: number;
    comment: string;
  },
) => {
  try {
    const response = await axiosInstance.post(
      `/ads/v2/order/${orderId}/evaluate`,
      params,
    );
    return response.data;
  } catch (error: any) {
    throw error?.message;
  }
};
// 查询评价
export const getReviewOrderApi = async (
  orderId: string,
  params: {
    paginate: {
      pageNum: number;
      pageLimit: number;
    };
  },
) => {
  try {
    const response = await axiosInstance.post(
      `/ads/v2/order/${orderId}/evaluations`,
      params,
    );
    return response.data;
  } catch (error: any) {
    throw error;
  }
};
// 编辑评价
export const editReviewOrderApi = async (
  orderId: string,
  params: {
    rating: number;
    comment: string;
  },
) => {
  try {
    const response = await axiosInstance.post(
      `/ads/v2/order/${orderId}/editEvaluation`,
      params,
    );
    return response.data;
  } catch (error: any) {
    throw error?.message;
  }
};
// 删除评价
export const deleteReviewOrderApi = async (orderId: string) => {
  try {
    const response = await axiosInstance.post(
      `/ads/v2/order/${orderId}/deleteEvaluation`,
      {},
    );
    return response.data;
  } catch (error: any) {
    throw error;
  }
};

// 获取待确认订单列表
export const getPendingInviteListApi = async (
  campaignId: string,
  params: {
    paginate: { pageNum: number; pageLimit: number };
    name: string;
    languages: string[];
    platforms: string[];
  },
) => {
  try {
    const response = await axiosInstance.post(
      `/ads/order/${campaignId}/pendingInvite`,
      params,
    );
    return response.data.data;
  } catch (error: any) {
    throw error?.message || error?.error;
  }
};
// 确认订单列表 批量邀约订单
export const batchConfirmInviteApi = async (params: {
  orderIds: string[];
  campaignId: string;
}) => {
  try {
    const response = await axiosInstance.post(`/ads/order/batchInvite`, params);
    return response.data.data;
  } catch (error: any) {
    throw error?.message || error?.error;
  }
};
// 确认订单列表 批量删除订单
export const batchDeleteInviteApi = async (params: {
  orderIds: string[];
  campaignId: string;
}) => {
  try {
    const response = await axiosInstance.post(
      `/ads/order/batchNotInvite`,
      params,
    );
    return response.data.data;
  } catch (error: any) {
    throw error?.message || error?.error;
  }
};
// 确认订单列表 确认全部订单
export const confirmAllInvitesApi = async (params: { campaignId: string }) => {
  try {
    const response = await axiosInstance.post(
      `/ads/order/batchInviteAll`,
      params,
    );
    return response.data.data;
  } catch (error: any) {
    throw error?.message || error?.error;
  }
};

// 下载确认订单列表
export const downloadConfirmedInviteListApi = async (
  campaignId: string,
  params: any,
) => {
  try {
    const response = await axiosInstance.post(
      `/ads/order/${campaignId}/pendingInvite/download`,
      params,
      { responseType: "blob" },
    );
    return response;
  } catch (error: any) {
    throw error;
  }
};
// 广告主确认达人后，设置账号信息
export const confirmAccessSetupApi = async (orderId: string) => {
  try {
    const response = await axiosInstance.post(
      `/ads/v2/order/${orderId}/completeAccountSetup`,
      {},
    );
    return response.data.data;
  } catch (error: any) {
    throw error?.message || error?.error;
  }
};
// 广告主报价
export const confirmRateApi = async (
  orderId: string,
  params: AdsNegotiateRequest,
) => {
  const url = `/ads/v2/order/${orderId}/negotiate`;
  try {
    const response = await axiosInstance.post(url, params);
    return response.data.data;
  } catch (error: any) {
    throw {
      code: error?.code,
      message: error?.message || error?.error,
    };
  }
};
// 提交达人链接
export const submitInfluencerLinkApi = async (
  orderId: string,
  params: {
    utmLink: string;
  },
) => {
  try {
    const response = await axiosInstance.post(
      `/ads/v2/order/${orderId}/utmLink`,
      params,
    );
    return response.data;
  } catch (error: any) {
    throw error?.message || error?.error;
  }
};
// 批量通过aha picks订单
export const batchConfirmAhaPicksApi = async (params: {
  orderIds: string[];
  campaignId: string;
}) => {
  try {
    const response = await axiosInstance.post(
      `/ads/order/ahaPicks/batchApprove`,
      params,
    );
    return response.data.data;
  } catch (error: any) {
    throw error?.message || error?.error;
  }
};
// 批量删除aha picks订单
export const batchRemoveAhaPicksApi = async (params: {
  orderIds: string[];
  campaignId: string;
}) => {
  try {
    const response = await axiosInstance.post(
      `/ads/order/ahaPicks/batchReject`,
      params,
    );
    return response.data.data;
  } catch (error: any) {
    throw error?.message || error?.error;
  }
};

export const submitNegotiationNoteApi = async (
  orderId: string,
  params: {
    lastBid: number;
    negotiationVersion: number;
    message: string;
    creatorTranslation?: CreatorTranslation;
  },
): Promise<void> => {
  await axiosInstance.post(`/ads/v2/order/${orderId}/negotiationNote`, params);
};

// 订单添加notes
export const updateOrderNotesApi = async (
  orderId: string,
  params: {
    note: string;
  },
) => {
  try {
    const response = await axiosInstance.post(
      `/ads/v2/order/${orderId}/note`,
      params,
    );
    return response.data.data;
  } catch (error: any) {
    throw error?.message || error?.error;
  }
};

// 修改订单发布时间
export const updateOrderPostTimeApi = async (
  orderId: string,
  params: {
    postTimeRange: number[];
    postTimeNote?: string;
    creatorTranslation?: CreatorTranslation;
  },
) => {
  try {
    const response = await axiosInstance.post(
      `/ads/v2/order/${orderId}/updatePostTime`,
      params,
    );
    return response.data;
  } catch (error: any) {
    return error;
  }
};
// 订单删除notes
export const deleteOrderNotesApi = async (orderId: string) => {
  try {
    const response = await axiosInstance.post(
      `/ads/v2/order/${orderId}/deleteNote`,
    );
    return response.data.data;
  } catch (error: any) {
    throw error?.message || error?.error;
  }
};

// 获取所选频道的总包参考价格范围
const inFlightPackageRecommendations = new Map<string, Promise<PackagePriceRecommendation>>();

export const getPackagePriceRecommendationApi = (
  orderId: string,
  params: PackagePriceRecommendationRequest,
): Promise<PackagePriceRecommendation> => {
  const requestKey = JSON.stringify([orderId, params.channels.map(({ channelKey, contentType }) => [channelKey, contentType])]);
  const existing = inFlightPackageRecommendations.get(requestKey);
  if (existing) return existing;
  // Share concurrent reads (including StrictMode effects), without caching results.
  const request = axiosInstance.post(
    `/ads/v2/order/${orderId}/packagePriceRecommendation`,
    params,
  ).then((response) => response.data.data)
    .finally(() => { inFlightPackageRecommendations.delete(requestKey); });
  inFlightPackageRecommendations.set(requestKey, request);
  return request;
};

// 获取参考价格范围
export const getFairPriceSuggestionApi = async (
  orderId: string,
  contentType?: string,
  campaignId?: number,
) => {
  try {
    const response = await axiosInstance.get(
      `/ads/order/${orderId}/fairPriceSuggestion`,
      {
        params: {
          ...(contentType ? { contentType } : {}),
          ...(campaignId != null ? { campaignId } : {}),
        },
      },
    );
    return response.data.data;
  } catch (error: any) {
    // axios 拦截器：业务错误 reject({ code, error })，HTTP 错误 reject({ message, ... })
    const msg = error?.message ?? error?.error;
    throw new Error(
      msg != null && String(msg).length > 0
        ? String(msg)
        : "getTodoOrderApi failed",
    );
  }
};

export type CollaborateAgainInviteType = "send_offer" | "request_quote";

export interface CollaborateAgainRequest {
  campaignId: number;
  inviteType: CollaborateAgainInviteType;
  offers?: Array<{ value: string; dealPrice: number }>;
  message?: string;
  creatorTranslation?: CreatorTranslation;
  nonNegotiable?: boolean;
}

export interface CollaborateAgainResponse {
  orderId: string;
  campaignId: number;
  cycleId: string;
  reSendSourceId: string;
}

export const collaborateAgainApi = async (
  sourceOrderId: string,
  params: CollaborateAgainRequest,
): Promise<CollaborateAgainResponse> => {
  try {
    const response = await axiosInstance.post(
      `/ads/v2/order/${sourceOrderId}/collaborateAgain`,
      params,
    );
    return response.data.data;
  } catch (error: any) {
    const msg = error?.message ?? error?.error;
    throw new Error(
      msg != null && String(msg).length > 0
        ? String(msg)
        : "Invite again failed",
    );
  }
};

export interface BatchCollaborateAgainRequest {
  sourceOrderIds: string[];
  campaignId: number;
  inviteType: "request_quote";
}

// 批量再次合作固定为邀请报价，来源 ID 必须是订单 ID。
export const batchCollaborateAgainApi = async (
  params: BatchCollaborateAgainRequest,
): Promise<void> => {
  try {
    await axiosInstance.post("/ads/order/batchCollaborateAgain", params);
    // TODO(bulk-reinvite-result): 若后端支持部分成功，需补充返回协议和逐单结果处理。
    // 当前沿用公共拦截器约定：code 非 0 会抛出，code 为 0 视为提交成功。
  } catch (error: any) {
    throw new Error(error?.message || error?.error || "Invite again failed");
  }
};

export interface InviteHistoryRecord {
  orderId: string;
  invitedAt: string;
  campaignId: number;
  campaignName: string;
}

// 历史接口以当前订单为入口；返回数据位于 data.list。
export const getInviteHistoryApi = async (
  orderId: string,
  signal?: AbortSignal,
): Promise<InviteHistoryRecord[]> => {
  try {
    const response = await axiosInstance.get(
      `/ads/order/${encodeURIComponent(orderId)}/inviteHistory`,
      { signal },
    );
    return response.data.data.list;
  } catch (error: any) {
    throw new Error(error?.message || error?.error || "Failed to load invite history");
  }
};
// 提交AhaPicks报价
export const submitAhaPicksOfferApi = async (
  orderId: string,
  params: {
    offers: { value: string; dealPrice: number }[];
    message: string;
    creatorTranslation?: CreatorTranslation;
    nonNegotiable: boolean;
  },
) => {
  try {
    const response = await axiosInstance.post(
      `/ads/order/${orderId}/ahaPicksOffer`,
      params,
    );
    return response.data.data;
  } catch (error: any) {
    throw error?.message || error?.error;
  }
};

// 打回达人提交的账号信息
export const accountInfoRequestChangeApi = async (orderId: string, params: {
  message: string;
  creatorTranslation?: CreatorTranslation;
}) => {
  try {
    const response = await axiosInstance.post(`/ads/v2/order/${orderId}/accountInfo/requestChange`, params);
    return response.data;
  } catch (error: any) {
    throw error?.message || error?.error;
  }
};
